/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * A remote_session record — Speakeasy's upstream OAuth session for a (principal, remote_session_client) pair. access_token_encrypted and refresh_token_encrypted are never returned.
 */
export type RemoteSession = {
  /**
   * Upstream access-token expiry. Independent of refresh_expires_at.
   */
  accessExpiresAt: Date;
  createdAt: Date;
  /**
   * Whether the session holds an upstream refresh token. Gates the 'Refresh now' action; refresh_expires_at is insufficient because an upstream may issue a non-expiring refresh token. The token itself is never returned.
   */
  hasRefreshToken: boolean;
  /**
   * The remote_session id.
   */
  id: string;
  /**
   * The upstream identity interface that supplied the stored account identity, such as an ID token or userinfo response. Absent when upstream identity is unknown.
   */
  identitySource?: string | undefined;
  /**
   * Upstream refresh-token expiry. Null when the session has no refresh token.
   */
  refreshExpiresAt?: Date | undefined;
  /**
   * The remote_session_client this session was minted against.
   */
  remoteSessionClientId: string;
  /**
   * Scopes held by this session.
   */
  scopes: Array<string>;
  /**
   * Resolved display name when the subject is a Speakeasy user. Absent for apikey/anonymous subjects or unresolved users.
   */
  subjectDisplayName?: string | undefined;
  /**
   * Resolved email when the subject is a Speakeasy user. Absent for apikey/anonymous subjects or unresolved users.
   */
  subjectEmail?: string | undefined;
  /**
   * The session's subject URN (user:<id> | apikey:<uuid> | anonymous:<mcp-session-id>).
   */
  subjectUrn: string;
  updatedAt: Date;
  /**
   * Stored display name of the account at the upstream provider. Absent when no upstream identity interface supplied it.
   */
  upstreamDisplayName?: string | undefined;
  /**
   * Stored email of the account at the upstream provider. Absent when no upstream identity interface supplied it; never inferred from the Speakeasy subject.
   */
  upstreamEmail?: string | undefined;
  /**
   * The user_session_issuer this session is bound to.
   */
  userSessionIssuerId: string;
};

/** @internal */
export const RemoteSession$inboundSchema: z.ZodMiniType<
  RemoteSession,
  unknown
> = z.pipe(
  z.object({
    access_expires_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    has_refresh_token: z.boolean(),
    id: z.string(),
    identity_source: z.optional(z.string()),
    refresh_expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    remote_session_client_id: z.string(),
    scopes: z.array(z.string()),
    subject_display_name: z.optional(z.string()),
    subject_email: z.optional(z.string()),
    subject_urn: z.string(),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    upstream_display_name: z.optional(z.string()),
    upstream_email: z.optional(z.string()),
    user_session_issuer_id: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "access_expires_at": "accessExpiresAt",
      "created_at": "createdAt",
      "has_refresh_token": "hasRefreshToken",
      "identity_source": "identitySource",
      "refresh_expires_at": "refreshExpiresAt",
      "remote_session_client_id": "remoteSessionClientId",
      "subject_display_name": "subjectDisplayName",
      "subject_email": "subjectEmail",
      "subject_urn": "subjectUrn",
      "updated_at": "updatedAt",
      "upstream_display_name": "upstreamDisplayName",
      "upstream_email": "upstreamEmail",
      "user_session_issuer_id": "userSessionIssuerId",
    });
  }),
);

export function remoteSessionFromJSON(
  jsonString: string,
): SafeParseResult<RemoteSession, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RemoteSession$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RemoteSession' from JSON`,
  );
}
